function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }

function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) { n[e] = r[e]; } return n; }

function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0) { ; } } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }

function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }

import { objectEntries, shallowClone, getType, isMatchOption, matchList, TraceContextInjection } from '@datadog/browser-core';
import { isSampled } from '../sampler/sampler';
import { createSpanIdentifier, createTraceIdentifier, toPaddedHexadecimalString } from './identifier';
export function isTracingOption(item) {
  var expectedItem = item;
  return getType(expectedItem) === 'object' && isMatchOption(expectedItem.match) && Array.isArray(expectedItem.propagatorTypes);
}
/**
 * Clear tracing information to avoid incomplete traces. Ideally, we should do it when the
 * request did not reach the server, but the browser does not expose this. So, we clear tracing
 * information if the request ended with status 0 without being aborted by the application.
 *
 * Reasoning:
 *
 * * Applications are usually aborting requests after a bit of time, for example when the user is
 * typing (autocompletion) or navigating away (in a SPA). With a performant device and good
 * network conditions, the request is likely to reach the server before being canceled.
 *
 * * Requests aborted otherwise (ex: lack of internet, CORS issue, blocked by a privacy extension)
 * are likely to finish quickly and without reaching the server.
 *
 * Of course, it might not be the case every time, but it should limit having incomplete traces a
 * bit.
 * */

export function clearTracingIfNeeded(context) {
  if (context.status === 0 && !context.isAborted) {
    context.traceId = undefined;
    context.spanId = undefined;
    context.traceSampled = undefined;
  }
}
export function startTracer(configuration, sessionManager, userContext, accountContext) {
  return {
    clearTracingIfNeeded: clearTracingIfNeeded,
    traceFetch: function traceFetch(context) {
      return injectHeadersIfTracingAllowed(configuration, context, sessionManager, userContext, accountContext, function (tracingHeaders) {
        var _a;

        if (context.input instanceof Request && !((_a = context.init) === null || _a === void 0 ? void 0 : _a.headers)) {
          context.input = new Request(context.input);
          Object.keys(tracingHeaders).forEach(function (key) {
            ;
            context.input.headers.append(key, tracingHeaders[key]);
          });
        } else {
          context.init = shallowClone(context.init);
          var headers = [];

          if (context.init.headers instanceof Headers) {
            context.init.headers.forEach(function (value, key) {
              headers.push([key, value]);
            });
          } else if (Array.isArray(context.init.headers)) {
            context.init.headers.forEach(function (header) {
              headers.push(header);
            });
          } else if (context.init.headers) {
            Object.keys(context.init.headers).forEach(function (key) {
              headers.push([key, context.init.headers[key]]);
            });
          }

          context.init.headers = headers.concat(objectEntries(tracingHeaders));
        }
      });
    },
    traceXhr: function traceXhr(context, xhr) {
      return injectHeadersIfTracingAllowed(configuration, context, sessionManager, userContext, accountContext, function (tracingHeaders) {
        Object.keys(tracingHeaders).forEach(function (name) {
          xhr.setRequestHeader(name, tracingHeaders[name]);
        });
      });
    }
  };
}

function injectHeadersIfTracingAllowed(configuration, context, sessionManager, userContext, accountContext, inject) {
  var session = sessionManager.findTrackedSession();

  if (!session) {
    return;
  }

  var tracingOption = configuration.allowedTracingUrls.find(function (tracingOption) {
    return matchList([tracingOption.match], context.url, true);
  });

  if (!tracingOption) {
    return;
  }

  var traceSampled = isSampled(session.id, configuration.traceSampleRate);
  var shouldInjectHeaders = traceSampled || configuration.traceContextInjection === TraceContextInjection.ALL;

  if (!shouldInjectHeaders) {
    return;
  }

  context.traceSampled = traceSampled;
  context.traceId = createTraceIdentifier();
  context.spanId = createSpanIdentifier();
  inject(makeTracingHeaders(context.traceId, context.spanId, context.traceSampled, session.id, tracingOption.propagatorTypes, userContext, accountContext, configuration));
}
/**
 * When trace is not sampled, set priority to '0' instead of not adding the tracing headers
 * to prepare the implementation for sampling delegation.
 */


function makeTracingHeaders(traceId, spanId, traceSampled, sessionId, propagatorTypes, userContext, accountContext, configuration) {
  var tracingHeaders = {};
  propagatorTypes.forEach(function (propagatorType) {
    switch (propagatorType) {
      case 'datadog':
        {
          Object.assign(tracingHeaders, {
            'x-datadog-origin': 'rum',
            'x-datadog-parent-id': spanId.toString(),
            'x-datadog-sampling-priority': traceSampled ? '1' : '0',
            'x-datadog-trace-id': traceId.toString()
          });
          break;
        }
      // https://www.w3.org/TR/trace-context/

      case 'tracecontext':
        {
          Object.assign(tracingHeaders, {
            traceparent: "00-0000000000000000".concat(toPaddedHexadecimalString(traceId), "-").concat(toPaddedHexadecimalString(spanId), "-0").concat(traceSampled ? '1' : '0'),
            tracestate: "dd=s:".concat(traceSampled ? '1' : '0', ";o:rum")
          });
          break;
        }
      // https://github.com/openzipkin/b3-propagation

      case 'b3':
        {
          Object.assign(tracingHeaders, {
            b3: "".concat(toPaddedHexadecimalString(traceId), "-").concat(toPaddedHexadecimalString(spanId), "-").concat(traceSampled ? '1' : '0')
          });
          break;
        }

      case 'b3multi':
        {
          Object.assign(tracingHeaders, {
            'X-B3-TraceId': toPaddedHexadecimalString(traceId),
            'X-B3-SpanId': toPaddedHexadecimalString(spanId),
            'X-B3-Sampled': traceSampled ? '1' : '0'
          });
          break;
        }
    }
  });

  if (configuration.propagateTraceBaggage) {
    var baggageItems = {
      'session.id': sessionId
    };
    var userId = userContext.getContext().id;

    if (typeof userId === 'string') {
      baggageItems['user.id'] = userId;
    }

    var accountId = accountContext.getContext().id;

    if (typeof accountId === 'string') {
      baggageItems['account.id'] = accountId;
    }

    var baggageHeader = Object.entries(baggageItems).map(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          value = _ref2[1];

      return "".concat(key, "=").concat(encodeURIComponent(value));
    }).join(',');

    if (baggageHeader) {
      tracingHeaders['baggage'] = baggageHeader;
    }
  }

  return tracingHeaders;
}